import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';

import ImageWithLoader from '@components/_shared/imageWithLoader';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { useIsMobile } from '@utils/hooks/useIsMobile';
import { BackButton } from '@components/_shared/backButton';

import styles from './styles.module.scss';
import { useScrollArrowsForContainer } from '@utils/hooks/useScrollArrowsForContainer';

type Props = {
  images: string[];
  isExpanded: boolean;
  toggleExpand: () => void;
  listingTitle: string;
  scrollToFloorplan?: boolean;
} & BaseComponentProps;

ListingPhotos.defaultProps = {
  images: [],
};

export function ListingPhotos(props: Props) {
  const {
    containerStyle,
    images,
    scrollToFloorplan,
    isExpanded,
    toggleExpand,
    listingTitle,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  const arrows = useScrollArrowsForContainer(
    containerRef,
    styles.imageViewer__scrollArrows
  );

  const scrollTo = (index: number) => {
    const image = document.getElementById(`image-${index}`);
    if (image) {
      image.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  const openImageViewer = useCallback((index: number) => {
    toggleExpand();
    setTimeout(() => {
      scrollTo(index);
    }, 50);
  }, []);

  useEffect(() => {
    if (scrollToFloorplan) {
      scrollTo(images.length - 1);
    }
  }, [scrollToFloorplan]);

  const imagesList = useMemo(() => {
    const list = [];
    for (let i = 0; i < images.length; ++i) {
      const key = `${i} - ${images[i]}`;
      if (i % 3 === 0) {
        list.push(
          <div key={key} className={styles.bigImageContainer}>
            <ImageWithLoader
              onClick={() => openImageViewer(i)}
              key={key}
              src={images[i]}
              alt={`image-${i}`}
              layout="fixed"
              height="580px"
              width="855px"
              containerStyle={styles.bigImage}
            />
          </div>
        );
      } else if (i < images.length) {
        const nextIndex = i + 1;
        list.push(
          <div key={key} className={styles.smallImageContainer}>
            <ImageWithLoader
              onClick={() => openImageViewer(i - 1)}
              key={key}
              src={images[i]}
              alt={`image-${i}`}
              layout="fixed"
              height="280px"
              width="420px"
              containerStyle={styles.smallImage}
            />
            {!!images[nextIndex] && (
              <ImageWithLoader
                onClick={() => openImageViewer(nextIndex)}
                key={nextIndex}
                src={images[nextIndex]}
                alt={`image-${nextIndex}`}
                layout="fixed"
                height="280px"
                width="420px"
                containerStyle={styles.smallImage}
              />
            )}
          </div>
        );
        i++;
      }
    }
    return list;
  }, [images, openImageViewer]);

  useEffect(() => {
    if (imagesList.length && containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
  }, [imagesList.length]);

  if (isExpanded) {
    return (
      <div className={styles.imageViewer}>
        <div className={styles.imageViewer__header}>
          <BackButton
            containerStyle={styles.imageViewer__backButton}
            onClick={toggleExpand}
          />

          <div className={styles.imageViewer__title}>
            Photos of <strong>{listingTitle}</strong>
          </div>
        </div>

        <div
          className={clsx([
            styles.galleryContainer,
            styles.imageViewer__thumbnails,
          ])}
        >
          {images.map((image, index) => (
            <ImageWithLoader
              id={`image-thumbnail--${index}`}
              key={index}
              src={image}
              alt={`image-thumbnail-${index}`}
              layout="fixed"
              width={200}
              height={113.4}
              objectFit="cover"
              onClick={() => scrollTo(index)}
              containerStyle={styles.imageViewer__thumbnail}
            />
          ))}
        </div>

        <div className={styles.imageViewer__images}>
          {images.map((image, index) => (
            <ImageWithLoader
              id={`image-${index}`}
              key={index}
              src={image}
              alt={`image-${index}`}
              layout="fill"
              objectFit="cover"
              containerStyle={styles.imageViewer__image}
              onClick={() => window.open(image, '_blank')}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx([styles.galleryContainer, containerStyle])}
      ref={containerRef}
    >
      {imagesList}

      {arrows}
    </div>
  );
}
